import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Button,
  DatePicker,
  message,
  Space,
  Table,
  Typography,
  Card,
  Statistic,
  Row,
  Col,
} from "antd";
import { PrinterOutlined, ArrowLeftOutlined } from "@ant-design/icons";
import { api } from "../../api/api";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";

const { Title } = Typography;
const { RangePicker } = DatePicker;

interface ProductionAllocation {
  _id: string;
  reportId: {
    _id: string;
    date: string;
    warehouseMgrName: string;
  };
  produceName: string;
  allocatedQuantity: number;
  allocationDate: string;
  ownerName: string;
  createdAt: string;
}

interface TaskOrder {
  id: string;
  produceName: string;
  total: number;
  produced: number;
  productionProgress: number;
}

export default function TaskOrderProductionAllocations() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [allocations, setAllocations] = useState<ProductionAllocation[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [total, setTotal] = useState<number>(0);
  const [totalAllocatedQuantity, setTotalAllocatedQuantity] = useState<number>(0);
  const [taskOrder, setTaskOrder] = useState<TaskOrder | null>(null);
  const [dateRange, setDateRange] = useState<
    [dayjs.Dayjs, dayjs.Dayjs] | undefined
  >([dayjs().startOf("month"), dayjs().endOf("month")]);

  const handleRangeChange = (dates: any) => {
    if (dates) {
      setDateRange([dates[0], dates[1]]);
    } else {
      setDateRange(undefined);
    }
    // Reset to first page when date range changes
    setPage(1);
  };

  const fetchAllocations = async () => {
    setLoading(true);

    try {
      const response = await api.get(`/task-order/${id}/production-allocations`, {
        params: {
          page,
          limit: pageSize,
          startDate: dateRange ? dateRange[0]?.format("DD.MM.YYYY") : undefined,
          endDate: dateRange ? dateRange[1]?.format("DD.MM.YYYY") : undefined,
        },
      });

      setAllocations(response.data.allocations);
      setTotal(response.data.total);
      setTotalAllocatedQuantity(response.data.totalAllocatedQuantity);
      setTaskOrder(response.data.taskOrder);
    } catch (error) {
      console.error("Error fetching production allocations", error);
      message.error("Ошибка при получении выделений производства");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAllocations();
  }, [page, pageSize, dateRange]);

  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    const currentDate = new Date().toLocaleDateString('ru-RU');
    const periodText = dateRange 
      ? `${dateRange[0].format('DD.MM.YYYY')} - ${dateRange[1].format('DD.MM.YYYY')}`
      : 'Все время';

    const printHtml = `
      <html>
        <head>
          <title>Выделения производства - ${taskOrder?.produceName || ''}</title>
          <style>
            body {
              font-family: Arial, sans-serif;
              font-size: 12px;
              margin: 20px;
              color: #222;
            }
            .header {
              text-align: center;
              margin-bottom: 20px;
              border-bottom: 2px solid #333;
              padding-bottom: 10px;
            }
            .header h1 {
              margin: 0;
              font-size: 18px;
              font-weight: bold;
            }
            .header .info {
              margin-top: 5px;
              font-size: 12px;
              color: #666;
            }
            .summary {
              margin: 20px 0;
              padding: 15px;
              border: 2px solid #1890ff;
              border-radius: 8px;
              background-color: #f0f8ff;
            }
            .summary h3 {
              margin: 0 0 10px 0;
              font-size: 14px;
              color: #1890ff;
            }
            .summary-grid {
              display: grid;
              grid-template-columns: repeat(3, 1fr);
              gap: 15px;
            }
            .summary-item {
              background: white;
              padding: 10px;
              border: 1px solid #d9d9d9;
              border-radius: 4px;
              text-align: center;
            }
            .summary-item .value {
              font-size: 18px;
              font-weight: bold;
              color: #1890ff;
            }
            .summary-item .label {
              font-size: 12px;
              color: #666;
              margin-top: 5px;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              margin-bottom: 20px;
              border: 2px solid #000;
            }
            th, td {
              border: 1px solid #000;
              padding: 8px;
              text-align: left;
              font-size: 11px;
            }
            th {
              background-color: #f0f0f0;
              font-weight: bold;
              text-align: center;
            }
            tr:nth-child(even) {
              background-color: #f9f9f9;
            }
            @media print {
              body { margin: 10px; }
              .header { page-break-after: avoid; }
              .summary { page-break-inside: avoid; }
            }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>ВЫДЕЛЕНИЯ ПРОИЗВОДСТВА</h1>
            <div class="info">
              <div>Продукция: ${taskOrder?.produceName || ''}</div>
              <div>Период: ${periodText}</div>
              <div>Дата печати: ${currentDate}</div>
              <div>Количество записей: ${allocations.length}</div>
            </div>
          </div>

          <div class="summary">
            <h3>📊 Сводка по заказу-наряду</h3>
            <div class="summary-grid">
              <div class="summary-item">
                <div class="value">${taskOrder?.total || 0}</div>
                <div class="label">Общее количество</div>
              </div>
              <div class="summary-item">
                <div class="value">${totalAllocatedQuantity}</div>
                <div class="label">Выделено из производства</div>
              </div>
              <div class="summary-item">
                <div class="value">${taskOrder?.productionProgress || 0}%</div>
                <div class="label">Прогресс производства</div>
              </div>
            </div>
          </div>
          
          <table>
            <thead>
              <tr>
                <th>№</th>
                <th>Дата выделения</th>
                <th>Отчет производства</th>
                <th>Складской менеджер</th>
                <th>Количество</th>
                <th>Пользователь</th>
              </tr>
            </thead>
            <tbody>
              ${allocations.map((allocation, index) => `
                <tr>
                  <td style="text-align: center;">${index + 1}</td>
                  <td>${allocation.allocationDate}</td>
                  <td>${allocation.reportId?.date || '-'}</td>
                  <td>${allocation.reportId?.warehouseMgrName || '-'}</td>
                  <td style="text-align: right; font-weight: bold;">${allocation.allocatedQuantity.toLocaleString('ru-RU', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}</td>
                  <td>${allocation.ownerName}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
        </body>
      </html>
    `;

    printWindow.document.write(printHtml);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
  };

  const columns = [
    {
      title: "Дата выделения",
      dataIndex: "allocationDate",
      key: "allocationDate",
    },
    {
      title: "Отчет производства",
      dataIndex: ["reportId", "date"],
      key: "reportDate",
    },
    {
      title: "Складской менеджер",
      dataIndex: ["reportId", "warehouseMgrName"],
      key: "warehouseMgrName",
    },
    {
      title: "Количество",
      dataIndex: "allocatedQuantity",
      key: "allocatedQuantity",
      render: (value: number) => value.toLocaleString('ru-RU', { 
        minimumFractionDigits: 0, 
        maximumFractionDigits: 2 
      }),
    },
    {
      title: "Пользователь",
      dataIndex: "ownerName",
      key: "ownerName",
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Space style={{ marginBottom: 16 }}>
        <Button 
          icon={<ArrowLeftOutlined />} 
          onClick={() => navigate(-1)}
        >
          Назад
        </Button>
        <Title level={2} style={{ margin: 0 }}>
          Выделения производства - {taskOrder?.produceName || ''}
        </Title>
      </Space>

      {taskOrder && (
        <Card style={{ marginBottom: 20 }}>
          <Row gutter={16}>
            <Col span={8}>
              <Statistic
                title="Общее количество"
                value={taskOrder.total}
                valueStyle={{ color: '#1890ff' }}
              />
            </Col>
            <Col span={8}>
              <Statistic
                title="Выделено из производства"
                value={totalAllocatedQuantity}
                valueStyle={{ color: '#52c41a' }}
              />
            </Col>
            <Col span={8}>
              <Statistic
                title="Прогресс производства"
                value={taskOrder.productionProgress}
                suffix="%"
                valueStyle={{ color: '#fa8c16' }}
              />
            </Col>
          </Row>
        </Card>
      )}
      
      <Space style={{ marginBottom: 16 }} wrap>
        <RangePicker
          onChange={handleRangeChange}
          placeholder={["Начало", "Конец"]}
          format="DD.MM.YY"
          value={dateRange}
        />
        <Button type="default" icon={<PrinterOutlined />} onClick={handlePrint}>
          Печать
        </Button>
      </Space>

      <Table
        columns={columns}
        dataSource={allocations}
        rowKey={(record) => record._id}
        pagination={{
          current: page,
          total: total,
          pageSize: pageSize,
          onChange: (newPage, newSize) => {
            setPage(newPage);
            if (newSize !== pageSize) {
              setPageSize(newSize!);
              setPage(1); // Reset to first page when page size changes
            }
          },
          showSizeChanger: true,
        }}
        scroll={{ x: "max-content" }}
        loading={loading}
      />
    </div>
  );
}
